import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Zap } from 'lucide-react';

interface PriorityBadgeProps {
  priority: number;
  reasons: Array<{ reason: string; weight: number }>;
  hasFutureFollowUp?: boolean;
  size?: 'sm' | 'md';
}

export function PriorityBadge({ 
  priority, 
  reasons, 
  hasFutureFollowUp = false,
  size = 'md' 
}: PriorityBadgeProps) {
  // Only show if Priority >= 60
  if (priority < 60) return null;
  
  // Determine color based on tier
  const getColorClass = () => {
    if (priority >= 90) {
      // Critical - amber/gold (like "Revenue at risk")
      return 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400';
    } else if (priority >= 70) {
      // High - blue (actionable, not danger)
      return 'border-primary/40 bg-primary/10 text-primary';
    } else {
      // Medium - gray/steel (control)
      return 'border-muted-foreground/40 bg-muted/10 text-muted-foreground';
    }
  };
  
  // Get top 2 reasons for tooltip
  const topReasons = reasons.slice(0, 3);
  
  const badgeContent = (
    <Badge
      variant="outline"
      className={cn(
        'font-semibold flex items-center gap-1',
        size === 'sm' ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5',
        getColorClass(),
        hasFutureFollowUp && 'opacity-75' // Reduce visual tension if follow-up scheduled
      )}
    >
      <Zap className={cn(size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
      Priority {priority}
    </Badge>
  );
  
  if (topReasons.length === 0) {
    return badgeContent;
  }
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {badgeContent}
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[200px] text-xs">
        <p className="font-semibold mb-1.5">Why it's high</p>
        <div className="space-y-1">
          {topReasons.map((r, idx) => (
            <p key={idx} className="text-muted-foreground">
              {r.reason} (+{r.weight})
            </p>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

